import {
  useGetRevisionsQuery,
  useRevertTourMutation,
} from '@carboncopyco/data';
import { RevertOutlineIcon } from '@carboncopyco/icons';
import {
  PermissionAction,
  PermissionKey,
  type Revision,
} from '@carboncopyco/types';
import { Button, ConfirmAlert, notify } from '@carboncopyco/ui';
import { AnalyticsTrackEventName, useAnalytics } from '@carboncopyco/utils';
import { useBoolean } from 'ahooks';
import { useState } from 'react';
import { usePermissionsContext } from '../../../utils';
import { useTourPageProps } from '../TourPageContext';

export const RevertButton = ({ revision }: { revision: Revision }) => {
  const { trackEvent } = useAnalytics();
  const { currRevision } = revision;
  const [openRevertModal, setOpenRevertModal] = useState(false);
  const [isDisabled, setIsDisabled] = useBoolean();
  const { organizationId, tourId } = useTourPageProps();
  const { data } = useGetRevisionsQuery({
    organizationId,
    sourceType: 'tour',
    id: String(tourId),
    reverseOrder: true,
    limit: 100,
  });
  const revisions = data?.items;
  const isLastRevision = revisions?.length === currRevision + 1;

  const { getPermissions } = usePermissionsContext();
  const revertTourRevisionPermission = getPermissions(
    PermissionKey.tour,
    PermissionAction.revertTour
  );

  const [revertTour] = useRevertTourMutation();

  if (isLastRevision) {
    return null;
  }
  const location = 'tour_info_page_revert_to_old_revision_modal';

  return (
    <div className="flex flex-row">
      {revertTourRevisionPermission && (
        <Button
          dark
          icon={<RevertOutlineIcon />}
          onClick={() => {
            trackEvent(AnalyticsTrackEventName.TOUR_CLICK_REVERT, {
              location: 'tour_info_page_history_tab_table_side_actions',
            });
            setOpenRevertModal(true);
          }}
        />
      )}
      <ConfirmAlert
        disabled={isDisabled}
        title="Revert to old revision?"
        containerClass="w-ds-modal-560"
        onConfirm={() => {
          trackEvent(AnalyticsTrackEventName.TOUR_CLICK_REVERT_APPROVE, {
            location,
          });
          setIsDisabled.setTrue();
          revertTour({
            organizationId,
            revisionId: currRevision,
            id: tourId,
          })
            .unwrap()
            .then(() => {
              notify.success('Success reverting tour');
            })
            .catch(() => {
              notify.error('Error reverting tour');
            })
            .finally(() => {
              setOpenRevertModal(false);
              setIsDisabled.setFalse();
            });
        }}
        onClose={() => {
          trackEvent(AnalyticsTrackEventName.TOUR_CLICK_REVERT_CANCEL, {
            location,
          });
          setOpenRevertModal(false);
        }}
        show={openRevertModal}
        description={`A new revision of this tour will be created with the content of revision #${
          currRevision + 1
        }`}
        noSecondButton
        yesLabel="Revert"
      />
    </div>
  );
};
